/* eslint-disable max-lines */
import React, {
  useState, useContext
} from 'react';
import {
  Button
} from 'shared/components/ui/button';
import {
  Label
} from 'shared/components/ui/label';
import {
  Input
} from 'shared/components/ui/input';
import {
  useParams
} from 'react-router-dom';
import {
  Context
} from 'shared/context/GlobalState';
import {
  channelService
} from 'shared/api';
import AssistantSelect from '../AssistantSelect';
import {
  FaCheck, FaCopy, FaPhone
} from 'react-icons/fa';
import {
  MdOutlineRecordVoiceOver
} from 'react-icons/md';
import {
  ChannelSetupLayout
} from '../ChannelSetupLayout';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import type {
  Channel
} from '../../types/builderChannel.types';

export interface VoiceChannelData {
  type: string;
  address: string;
  topic: string;
  assistantId: string;
  organizationId?: string;
  subOrganizationId?: string;
  _id?: string;
}

// Kept for VoiceUpdate backward compatibility
export const VoiceChannelInput = ({
  newChannelObj = {}, onSubmit, setOpenEmailModal, isUpdate = false
}) => {
  const [btnLoading, setBtnLoading] = useState(false);
  const {
    oragID, subOragID
  } = useParams();
  // Org-scoped callback URL. FEO rejects the org-less form with a 400 (#427, and
  // #407 before it): without :organizationId the channel lookup runs with no tenant
  // filter and can resolve another org’s — or another environment’s — channel.
  const FEO_CALLBACK_URL = `${process.env.REACT_APP_FEO_SERVER_URL}/feo-v3/api/voice/receiveCall/${oragID}`;
  const [channelData, setChannelData] = useState({
    type: 'voice',
    address: 'Number:+',
    topic: '',
    assistantId: '',
    ...newChannelObj,
  });
  const [copied, setCopied] = useState(false);

  const handleChange = (value, type) => setChannelData((prev) => ({
    ...prev,
    [type]: value
  }));
  const handleNumberChange = (e) => setChannelData((prev) => ({
    ...prev,
    address: `Number:+${e.target.value}`
  }));
  const handleCopyClick = () => {
    navigator.clipboard.writeText(FEO_CALLBACK_URL).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  const isButtonDisabled = () =>
    channelData.topic.trim() === '' ||
    channelData.address.trim() === 'Number:+' ||
    !channelData.assistantId;

  const handleSubmit = () => {
    setBtnLoading(true);
    onSubmit(channelData).finally(() => setBtnLoading(false));
  };

  return (
      <ChannelSetupLayout
      onCancel={() => setOpenEmailModal(false)}
      onSubmit={handleSubmit}
      submitDisabled={isButtonDisabled()}
      loading={btnLoading}
      className="flex flex-col gap-4"
    >
          <div>
              <button
          onClick={() => window.open('https://coolriots.ai/voice-channel/')}
          className="text-foreground hover:text-muted-foreground cursor-pointer text-left"
        >
                  <span className="flex items-center">
                      <MdOutlineRecordVoiceOver className="text-foreground mr-2" size={20} />
                      How to Create a Voice Channel
                  </span>
              </button>
          </div>
          <div className="flex flex-col gap-2">
              <Label>Number <span className="text-red-500">*</span></Label>
              <div className="flex items-center border rounded-md bg-muted">
                  <span className="px-3 py-2 text-muted-foreground font-mono text-xs">Number:+</span>
                  <Input
            disabled={isUpdate}
            className="flex-1 border-none bg-background"
            value={channelData.address.replace('Number:+', '')}
            onChange={handleNumberChange}
            placeholder="1234567890"
            type="tel"
          />
              </div>
          </div>
          <div className="flex flex-col gap-2">
              <Label>Topic <span className="text-red-500">*</span></Label>
              <Input
          value={channelData.topic}
          onChange={(e) => handleChange(e.target.value, 'topic')}
          placeholder="Customer Support, Sales, etc."
        />
          </div>
          {!(newChannelObj as any)?.isAssistantDisabled && (
          <div className="flex flex-col gap-2">
              <Label>Assistant <span className="text-red-500">*</span></Label>
              <AssistantSelect
          value={channelData.assistantId}
          onChange={(value) => handleChange(value, 'assistantId')}
          placeholder="Select Assistant to connect to"
        />
          </div>
          )}
          <div className="flex flex-col gap-2">
              <Label>
                  Callback URL{' '}
                  <span className="text-muted-foreground text-xs">(copy and paste in Twilio)</span>
              </Label>
              <div className="flex items-center gap-2">
                  <Input readOnly className="bg-muted" value={FEO_CALLBACK_URL} />
                  <Button type="button" variant="ghost" size="icon" onClick={handleCopyClick}>
                      {copied ? <FaCheck className="text-green-500" /> : <FaCopy />}
                  </Button>
              </div>
          </div>
      </ChannelSetupLayout>
  );
};

interface VoiceSetupProps {
  setOpenEmailModal: (open: boolean) => void;
  setEmailChannels?: React.Dispatch<React.SetStateAction<Channel[]>>;
  onBack?: () => void;
  isUpdate?: boolean;
  newChannelObj?: Partial<VoiceChannelData>;
  onSubmit?: (channel: VoiceChannelData) => Promise<void>;
}

export default function VoiceSetup({
  setOpenEmailModal,
  setEmailChannels,
  onBack,
  isUpdate = false,
  newChannelObj,
  onSubmit,
}: VoiceSetupProps) {
  const {
    oragID, subOragID
  } = useParams();
  // Org-scoped callback URL. FEO rejects the org-less form with a 400 (#427, and
  // #407 before it): without :organizationId the channel lookup runs with no tenant
  // filter and can resolve another org’s — or another environment’s — channel.
  const FEO_CALLBACK_URL = `${process.env.REACT_APP_FEO_SERVER_URL}/feo-v3/api/voice/receiveCall/${oragID}`;
  const {
    addNewNotifcation
  } = useContext(Context);
  const [btnLoading, setBtnLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [channelData, setChannelData] = useState({
    type: 'voice',
    address: 'Number:+',
    topic: '',
    assistantId: '',
    ...newChannelObj,
  });
  const handleChange = (value: string, type: string) =>
    setChannelData((prev) => ({
      ...prev,
      [type]: value
    }));

  const handlePhoneChange = (value: string) =>
    setChannelData((prev) => ({
      ...prev,
      address: `Number:+${value}`
    }));

  const handleCopyClick = () => {
    navigator.clipboard.writeText(FEO_CALLBACK_URL).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const isButtonDisabled = () =>
    channelData.topic.trim() === '' ||
    channelData.address.trim() === 'Number:+' ||
    !channelData.assistantId;

  const handleSubmit = async () => {
    if (isUpdate && onSubmit) {
      setBtnLoading(true);
      try {
        await onSubmit(channelData as VoiceChannelData);
      } finally {
        setBtnLoading(false);
      }
      return;
    }

    setBtnLoading(true);
    try {
      const payload = {
        org_id: oragID || '',
        sub_org_id: subOragID || '',
        address: channelData.address,
        topic: channelData.topic,
        type: channelData.type,
        provider: 'native',
        assistant_id: channelData.assistantId || '',
        receive_enabled: true,
        send_mode: 'auto' as const,
      };
      const res = await channelService.addChannel(payload);
      addNewNotifcation('Channel added successfully', 'success');
      setOpenEmailModal(false);
      setEmailChannels?.((prev) => [res.data, ...prev]);
    } catch (error: any) {
      if (error.response?.status === 409) {
        addNewNotifcation('Channel already exists', 'danger');
      } else if (error.response?.status === 403) {
        addNewNotifcation(
          error?.response?.data?.message || 'Resource limit. You can not create more channels under your current plan',
          'danger'
        );
      } else {
        addNewNotifcation('Something went wrong', 'danger');
      }
    } finally {
      setBtnLoading(false);
    }
  };

  return (
      <ChannelSetupLayout
      onCancel={() => setOpenEmailModal(false)}
      onSubmit={handleSubmit}
      submitDisabled={isButtonDisabled()}
      loading={btnLoading}
      onBack={isUpdate ? undefined : onBack}
      title={isUpdate ? 'Update Voice Channel' : 'Configure Voice Channel'}
      isDashboard={true}
    >
          <div className="flex flex-col sm:flex-row w-full">
              <aside className="sm:w-[320px] shrink-0 bg-muted/40 border-b sm:border-b-0 sm:border-r border-border p-6 flex flex-col gap-5">
                  <div className="space-y-1">
                      <div className="inline-flex items-center gap-2 px-2 py-1 bg-primary/10 rounded-md text-primary mb-1">
                          <FaPhone size={12} />
                          <span className="text-[10px] font-bold uppercase tracking-wider">Channel Configuration</span>
                      </div>
                      <h3 className="text-sm font-semibold text-foreground px-1">Configure Instance</h3>
                  </div>
                  <div className="flex flex-col gap-4">
                      <div className="flex flex-col gap-1">
                          <Label className="text-[11px] font-semibold text-muted-foreground ml-1">
                              Display Name <span className="text-red-500">*</span>
                          </Label>
                          <Input
                value={channelData.topic}
                onChange={(e) => handleChange(e.target.value, 'topic')}
                placeholder="Customer Support"
                className="h-9 text-xs"
              />
                      </div>
                      {!(newChannelObj as any)?.isAssistantDisabled && (
                      <div className="flex flex-col gap-1.5">
                          <Label className="text-[11px] font-semibold text-muted-foreground ml-1">
                              AI Assistant <span className="text-red-500">*</span>
                          </Label>
                          <AssistantSelect
                className="mt-0.5"
                value={channelData.assistantId}
                onChange={(value: string) => handleChange(value, 'assistantId')}
              />
                      </div>
                      )}
                      <div className="flex flex-col gap-1.5">
                          <Label className="text-[11px] font-semibold text-muted-foreground ml-1">
                              Phone Number <span className="text-red-500">*</span>
                          </Label>
                          <PhoneInput
                country={'my'}
                value={channelData.address.replace('Number:+', '') || ''}
                onChange={handlePhoneChange}
                disabled={isUpdate}
                inputClass="!w-full !h-10 !pl-10 !border-border !rounded-md !text-xs !bg-background !text-foreground"
                containerClass="!w-full"
                specialLabel=""
              />
                      </div>
                  </div>
                  <div className="mt-auto pt-2">
                      <button
              type="button"
              onClick={() => window.open('https://coolriots.ai/voice-channel/', '_blank')}
              className="text-[10px] font-medium text-muted-foreground hover:text-primary transition-colors flex items-center gap-1.5 px-1 underline underline-offset-4"
            >
                          View Documentation
                      </button>
                  </div>
              </aside>

              <main className="flex-grow bg-background p-8 flex flex-col items-center justify-center">
                  <div className="w-full max-w-sm space-y-6 text-center animate-in fade-in slide-in-from-bottom-2 duration-500">
                      <div className="w-16 h-16 bg-primary/10 text-primary rounded-2xl flex items-center justify-center mx-auto">
                          <MdOutlineRecordVoiceOver size={32} />
                      </div>
                      <div className="space-y-2">
                          <h4 className="text-lg font-bold text-foreground">Twilio Voice Webhook</h4>
                          <p className="text-sm text-muted-foreground">
                              Configure your Twilio phone number with the callback URL below to receive inbound calls.
                          </p>
                      </div>
                      <div className="flex items-center gap-2 p-1.5 bg-muted border border-border rounded-lg">
                          <code className="flex-grow text-[11px] text-muted-foreground font-mono px-2 overflow-hidden text-ellipsis whitespace-nowrap">
                              {FEO_CALLBACK_URL}
                          </code>
                          <Button type="button" variant="ghost" size="icon" onClick={handleCopyClick} className="h-8 w-8">
                              {copied
                                ? <FaCheck className="text-green-500 size-3" />
                                : <FaCopy className="text-muted-foreground size-3" />}
                          </Button>
                      </div>
                  </div>
              </main>
          </div>
      </ChannelSetupLayout>
  );
}
